import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "@vds/buttons";
import { Icon } from "@vds/icons";
import { FilterMatchMode } from "primereact/api";
import styled from "styled-components";
import moreIcon from "../../assets/icons/more.png";
import StatusBadge from "../../components/core/StatusBadge/StatusBadge";
import { Loader } from "@vds/loaders";
import { getJobsV3 } from "../../services/scheduler/getJobs";
import { handleAddPage, switchTab } from "../C4S Decommission - Replacement/utils";
import ThreeDotsTemplate from "./ThreeDotsTemplate";
import { JobsMetadataDetails } from "./JobsMetaData";
import { getDropDownData } from "../../services/scheduler/getDropDownData";
import NotificationBanner from "../../components/NotificationBanner/NotificationBanner";
import { addPage } from "../../../utility/navigation";
import { getOpenJobsTabIndexFromSession } from "../C4S Decommission - Replacement/navigation/tabHelpers";
import { AlternateManualEntryMetadata } from "../OpenJobs/AlternateManualSchedule";

const ALL_ROWS = 9999;

const StyledDataTable = styled(DataTable)`
  .p-datatable-wrapper {
    border: 1px solid #dddad4;
    border-radius: 8px;
    overflow: hidden;
  }
  .p-datatable-thead > tr > th {
    background-color: #ffffff;
    color: #000000;
    font-weight: 700;
    font-family: "Verizon-NHG-eDS";
    border-bottom: 2px solid #000000 !important;
    font-size: 14px;
  }
  .p-datatable-thead > tr > th:hover {
    background-color: #f8f7f5;
    color: #000000;
  }
  .p-sortable-column .p-sortable-column-icon {
    visibility: hidden;
    color: #000000;
  }
  .p-sortable-column:hover .p-sortable-column-icon,
  .p-sortable-column.p-highlight .p-sortable-column-icon {
    visibility: visible;
    color: #000000 !important;
  }
  .p-sortable-column.p-highlight,
  .p-sortable-column.p-highlight:hover,
  .p-sortable-column.p-highlight .p-column-title,
  .p-sortable-column.p-highlight:hover .p-column-title {
    color: #000000 !important;
  }
  .p-datatable-tbody > tr > td {
    border-bottom: 1px solid #e0e0e0;
    padding: 8px 10px !important;
    color: #000000 !important;
    font-family: "Verizon-NHG-eDS" !important;
    font-size: 12px;
  }
  .p-datatable-tbody > tr:hover {
    background-color: #fafafa;
  }
  .p-datatable-header {
    border: none;
    background: #fff;
    padding: 0;
    color: black;
  }
  .p-datatable-emptymessage > tr > td {
    text-align: center !important;
    padding: 2rem 1rem !important;
  }
  .p-paginator {
    padding: 8px 16px;
    display: flex;
    align-items: center;
  }
  .p-paginator-page {
    background: none !important;
    border: none !important;
    box-shadow: none !important;
    font-size: 12px !important;
    font-family: "Verizon-NHG-eDS" !important;
    min-width: unset !important;
    padding: 0 4px !important;
    color: #000 !important;
  }
  .p-paginator-page.p-highlight {
    background: none !important;
    font-weight: 700 !important;
  }
  .p-paginator,
  .p-paginator.p-component,
  .p-paginator-bottom {
    border: 0 !important;
    border-top: 0 !important;
    border-bottom: 0 !important;
    box-shadow: none !important;
  }
`;

const statusTemplate = (rowData) => {
  return <StatusBadge status={rowData.status} />;
};

const installationAddressTemplate = (rowData) => {
  const { address, address1, city, stateCode, country, zipCode, contactName } = rowData.installationAddress;
  const streetPart = [address, address1].filter(Boolean).join(", ");
  const cityPart = `${city ? `${city}, ` : ""}${stateCode || ""} ${zipCode || ""}${country ? `, ${country}` : ""}`;

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
      <span>
        {streetPart} {cityPart}`{" "}
      </span>
      <span>
        <strong>Contact:</strong> {contactName}
      </span>
    </div>
  );
};

const formatDateAndTime = (value) => {
  if (!value) {
    return { dateText: "-", timeText: "-" };
  }

  const parsedDate = new Date(value);
  if (!Number.isNaN(parsedDate.getTime())) {
    return {
      dateText: parsedDate.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      }),
      timeText: parsedDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  }

  if (typeof value === "string") {
    const matches = value.match(/^([A-Za-z]{3}\s\d{2},\s\d{4})\s(.+)$/);
    if (matches) {
      return { dateText: matches[1], timeText: matches[2] };
    }
  }

  return { dateText: `${value}`, timeText: "-" };
};

const formatDuration = (minutes) => {
  if (minutes == null || minutes === "") {
    return "";
  }

  const totalMinutes = Number(minutes);
  if (Number.isNaN(totalMinutes)) {
    return "";
  }

  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const hourPart = hours > 0 ? `${hours} hr` : "";
  const minutePart = mins > 0 ? `${mins} min` : "";
  return [hourPart, minutePart].filter(Boolean).join(" ");
};

const installationDateTemplate = (rowData) => {
  const { dateText } = formatDateAndTime(rowData.installationDate);
  const timeText = rowData.installationStartTime ? `${rowData.installationStartTime} - ${rowData.installationEndTime}` : "";
  const durationText = formatDuration(rowData.installationDuration);
  const secondLine = durationText ? `${timeText} (${durationText})` : timeText;

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
      <span>{dateText}</span>
      <span>{secondLine}</span>
    </div>
  );
};

const lastUpdatedTemplate = (rowData) => {
  const { dateText, timeText } = formatDateAndTime(rowData.lastUpdated);
  const hasLastUpdatedBy = Boolean(rowData?.lastUpdatedBy);

  const content = (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
      <span>{dateText}</span>
      <span>{timeText}</span>
    </div>
  );

  if (!hasLastUpdatedBy) {
    return content;
  }

  return (
    <span title={`Last Updated By: ${rowData.lastUpdatedBy}`} css={{ cursor: "pointer" }}>
      {content}
    </span>
  );
};

const actionsTemplate = () => {
  return (
    <div css={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <button
        type="button"
        disabled
        aria-label="Actions"
        css={{ border: "none", background: "transparent", padding: 0, cursor: "default", display: "inline-flex", alignItems: "center" }}
      >
        <img src={moreIcon} alt="More actions" css={{ width: "16px", height: "16px", objectFit: "contain" }} />
      </button>
    </div>
  );
};

const Jobs = (props) => {
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [jobDetails, setJobDetails] = useState([]);
  const [jobApiError, setJobApiError] = useState("");
  const [banner, setBanner] = useState(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(5);
  const [jumpPageInput, setJumpPageInput] = useState("1");
  const isMountedRef = useRef(true);
  const totalPages = Math.max(1, Math.ceil(jobDetails.length / rows));
  const currentPage = Math.floor(first / rows) + 1;

  const showBanner = useCallback((type, title, message) => {
    setBanner({ type, title, message });
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (isMountedRef.current) {
        setLoading(true);
      }
      const response = await getJobsV3({ accountId: props.AccountID, cspOrCaseId: props.ID });
      if (!isMountedRef.current) {
        return;
      }

      if (response.status === 200 && response?.data?.data) {
        const jobData = response?.data?.data.filter((job) => !(job?.scheduleType === "AUTO" && ["OPEN", "CLOSED"].includes(job?.status)));
        setJobDetails(jobData);
      } else {
        setJobDetails([]);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    setJumpPageInput(String(currentPage));
  }, [currentPage]);

  const fetchJobDetails = async (jobId) => {
    try {
      if (isMountedRef.current) {
        setLoading(true);
      }
      const response = await getDropDownData(jobId);
      if (!isMountedRef.current) {
        return;
      }
      const responsedata = response?.data?.data?.[0];
      if (response.status === 200 && responsedata) {
        navigateToManualSchedule(responsedata);
        setJobApiError("");
      } else {
        throw new Error(response.response?.data.message || "Failed to fetch job details");
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      setJobApiError(error.message);
      showBanner("error", "Failed to fetch Job details", error.message);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const navigateToManualSchedule = (jobData) => {
    const cspSessionData = props?.cspData?.data?.data || JSON.parse(sessionStorage.getItem("CSPDetails"));
    switchTab(getOpenJobsTabIndexFromSession(), { data: "WT" });
    addPage(AlternateManualEntryMetadata.id, {
      jobData: {
        ...jobData,
        contractAccount: cspSessionData?.CONTRACT_ACCOUNT_ID?.[0] || "",
        isReschedule: false,
        fromAutoSchedule: true,
        salesOrg: jobData.salesOrg ? jobData.salesOrg : "",
      },
      mapUrl: "",
    });
  };

  const onHyperLinkClicked = (data) => {
    handleAddPage(JobsMetadataDetails.id, {
      data,
      AccountId: props.AccountID,
      cspId: props.ID,
      userId: props.userId,
    });
  };

  const handleViewDetails = (data) => {
    handleAddPage(JobsMetadataDetails.id, {
      data,
    });
  };

  const threeDotsTemplate = (rowData) => {
    return <ThreeDotsTemplate caseDetails={rowData} handleViewDetails={handleViewDetails} />;
  };

  const jobIdTemplate = (rowData) => {
    return (
      <>
        {["scheduled", "po released", "rescheduled", "po released-all", "partially completed", "cancelled", "completed"].includes(
          rowData.status.toLowerCase()
        ) ? (
          <div
            style={{ fontWeight: "400", textDecoration: "underline", cursor: "pointer", color: "#006FC0" }}
            onClick={() => onHyperLinkClicked(rowData)}
          >
            {rowData.id}
          </div>
        ) : rowData?.status.toLowerCase() === "open" && rowData?.scheduleType.toLowerCase() === "manual" ? (
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              fetchJobDetails(rowData.id);
            }}
            style={{ color: "blue", textDecoration: "underline" }}
            aria-label={`Job ${rowData.id}`}
          >
            {rowData.id}
          </a>
        ) : (
          <div style={{ fontWeight: "400" }}>{rowData.id}</div>
        )}
      </>
    );
  };

  const goToPage = (pageNumber) => {
    const safePage = Math.min(Math.max(1, pageNumber), totalPages);
    setFirst((safePage - 1) * rows);
    setJumpPageInput(String(safePage));
  };

  const paginatorTemplate = {
    layout: "JumpToPageInput CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink",
    JumpToPageInput: (options) => (
      <span style={{ display: "flex", color: "black", alignItems: "center", gap: "6px", fontSize: "16px", fontFamily: "Verizon-NHG-eDS" }}>
        <span>Go to:</span>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={jumpPageInput}
          onChange={(e) => setJumpPageInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const page = Number(jumpPageInput);
              if (!Number.isNaN(page) && page > 0) goToPage(page);
            }
          }}
          onBlur={() => {
            const page = Number(jumpPageInput);
            if (!Number.isNaN(page) && page > 0) {
              goToPage(page);
            } else {
              setJumpPageInput(String(currentPage));
            }
          }}
          style={{ width: "40px", padding: "2px 4px", border: "1px solid black", borderRadius: "4px", textAlign: "center", fontSize: "16px" }}
        />
      </span>
    ),
    CurrentPageReport: (options) => {
      const start = options.totalRecords === 0 ? 0 : options.first;
      const end = Math.min(options.last, options.totalRecords);
      return (
        <span style={{ flex: 1, textAlign: "left", fontSize: "16px", color: "black", paddingLeft: "4px", fontFamily: "Verizon-NHG-eDS" }}>
          Displaying items {start}-{end} of {options.totalRecords}
        </span>
      );
    },
    RowsPerPageDropdown: (options) => {
      const rowChoices = [
        { label: "All", value: ALL_ROWS },
        { label: "5", value: 5 },
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
      ];
      return (
        <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "16px", color: "black", fontFamily: "Verizon-NHG-eDS" }}>
          <span>Items per page: </span>
          <select
            value={options.value >= ALL_ROWS ? ALL_ROWS : options.value}
            onChange={(e) => options.onChange({ value: Number(e.target.value) })}
            style={{ border: "1px solid #ccc", borderRadius: "4px", padding: "2px 4px", fontSize: "16px", cursor: "pointer" }}
          >
            {rowChoices.map((c) => (
              <option key={c.label} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </span>
      );
    },
    PrevPageLink: (options) => (
      <button
        type="button"
        onClick={options.onClick}
        disabled={options.disabled}
        style={{
          background: "none",
          border: "none",
          cursor: options.disabled ? "default" : "pointer",
          color: options.disabled ? "#ccc" : "#000",
          fontSize: "16px",
          padding: "0 2px",
        }}
      >
        ←
      </button>
    ),
    PageLinks: (options) => options.element,
    NextPageLink: (options) => (
      <button
        type="button"
        onClick={options.onClick}
        disabled={options.disabled}
        style={{
          background: "none",
          border: "none",
          cursor: options.disabled ? "default" : "pointer",
          color: options.disabled ? "#ccc" : "#000",
          fontSize: "16px",
          padding: "0 2px",
        }}
      >
        →
      </button>
    ),
  };

  const filters = useMemo(
    () => ({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    }),
    []
  );

  const emptyMessageTemplate = () => {
    const isSearching = globalFilterValue.trim().length > 0;
    const displayedJobs = jobDetails.filter((job) =>
      Object.values(job).some((value) => value && value.toString().toLowerCase().includes(globalFilterValue.toLowerCase()))
    );
    const hasResults = displayedJobs.length > 0;

    return (
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          padding: "2rem 1rem",
        }}
      >
        <p css={{ margin: 0, fontSize: "1rem", color: "#666" }}>
          {isSearching && !hasResults ? "No matching jobs." : "No jobs assigned to this order yet"}
        </p>
        {isSearching && !hasResults && (
          <div css={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <Button size="small" secondary onClick={() => setGlobalFilterValue("")} style={{ marginRight: "10px" }}>
              Clear Search
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div
        css={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          padding: "0.5rem 0 1rem",
          flexDirection: "row",
        }}
      >
        <div css={{ fontSize: "24px", fontWeight: 700 }}>Jobs</div>
        <div css={{ position: "relative", width: "21rem", maxWidth: "100%" }}>
          <span
            css={{
              position: "absolute",
              right: "0.75rem",
              top: "50%",
              transform: "translateY(-50%)",
              display: "inline-flex",
              alignItems: "center",
              pointerEvents: "none",
              zIndex: 1,
            }}
          >
            <Icon name="search" size="small" color="#000000" aria-hidden="true" />
          </span>
          <InputText
            value={globalFilterValue}
            onChange={(e) => setGlobalFilterValue(e.target.value)}
            placeholder="Search Jobs, Install Partner or POs..."
            style={{ width: "100%", borderRadius: "8px", border: "1px solid #ced4da", paddingRight: "2.25rem" }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {loading ? (
        <Loader active={loading} />
      ) : (
        <>
          {jobApiError ? (
            <div style={{ margin: "12px 0 16px 0" }}>
              <NotificationBanner type={banner.type} title={banner.title} message={banner.message} />
            </div>
          ) : (
            <div css={{ padding: "1rem", background: "#fff", fontFamily: "Verizon-NHG-eDS", color: "black" }}>
              <StyledDataTable
                value={jobDetails}
                header={renderHeader()}
                globalFilterFields={["id", "installPartner", "purchaseOrder"]}
                filters={filters}
                globalFilter={globalFilterValue}
                sortMode="single"
                sortField="id"
                sortOrder={-1}
                paginator
                paginatorTemplate={paginatorTemplate}
                first={first}
                rows={rows}
                onPage={(e) => {
                  setFirst(e.first);
                  setRows(e.rows);
                }}
                rowsPerPageOptions={[ALL_ROWS, 5, 10, 20, 50]}
                responsiveLayout="scroll"
                emptyMessage={emptyMessageTemplate()}
                stripedRows
                className="p-datatable-sm"
              >
                <Column field="id" header="Job ID" body={jobIdTemplate} sortable />
                <Column field="status" header="Status" body={statusTemplate} sortable />
                <Column field="lastUpdated" header="Last Updated" body={lastUpdatedTemplate} sortable />
                <Column field="installationDate" header="Installation Date" body={installationDateTemplate} sortable />
                <Column field="installationAddress" header="Installation Address" body={installationAddressTemplate} />
                <Column field="vehicles" header="Vehicles" />
                <Column field="installPartner" header="Install Partner" />
                <Column field="purchaseOrder" header="Purchase Order" />
                <Column header="Actions" body={threeDotsTemplate} style={{ width: "5rem" }} />
              </StyledDataTable>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Jobs;
